// Curated word-equivalence groups for the Bible-search related-wording fallback
// (08.5). When an exact verse query returns ZERO hits because BSB uses a
// different word than the user remembered, the related pass (bsb-search-core)
// retries with these substitutions BEFORE the fuzzy pass and labels the result
// honestly. Gated to zero-exact, exactly like fuzzy (rule #38), so precision on
// a good query is never touched.
//
// Probe-importable leaf: its ONLY runtime import is the zero-import
// normalizeForSearch, so bsb-search-core stays probe-importable when it imports
// this. en-US only at v1 (rule #7).
//
// O4 RATIFIED (operator): Tier 1 ONLY — the two safest translation-variance
// pairs (a KJV word the user is likely to remember -> the BSB word). Tier 2
// (worry/anxious/anxiety, afraid/fear/dread) + Tier 3 are DEFERRED to post-v1,
// gated on the real missed-search metrics this arc emits (relatedAttemptedTerms).
// A bad substitution would surface a wrong verse on a zero-exact query, so the
// list stays small + theologically safe. See docs/plans/bible-search/08.5-plan.md.

import { normalizeForSearch } from '../search-normalize';

// Equivalence GROUPS (bidirectional): each word expands to the OTHER members of
// its group. NOT a directional map.
export const WORD_RELATION_GROUPS: readonly (readonly string[])[] = [
  ['works', 'deeds'], // KJV "works" / BSB "deeds" (James 2) — the gold case
  ['charity', 'love'], // KJV "charity" / BSB "love" (1 Cor 13)
];

/**
 * Builds the normalized `word -> alternatives[]` index. PURE (no module state),
 * so the probe can validate bad fixtures without touching the live map. THROWS
 * on a malformed group (mirrors bsb-parser's alias self-check, so a typo in the
 * curated list surfaces at module load / probe time, never silently):
 *   - a group with fewer than 2 terms (post-normalize),
 *   - an empty / whitespace-only term,
 *   - a duplicate normalized word WITHIN one group,
 *   - a duplicate normalized word ACROSS groups.
 */
export function validateRelationGroups(
  groups: readonly (readonly string[])[],
): Map<string, string[]> {
  const index = new Map<string, string[]>();
  for (const group of groups) {
    const norm = group.map((t) => normalizeForSearch(t));
    for (const t of norm) {
      if (t === '') {
        throw new Error(
          `word-relations: empty/whitespace term in group ${JSON.stringify(group)}`,
        );
      }
    }
    if (norm.length < 2) {
      throw new Error(
        `word-relations: group needs >= 2 terms: ${JSON.stringify(group)}`,
      );
    }
    const within = new Set<string>();
    for (const t of norm) {
      if (within.has(t)) {
        throw new Error(`word-relations: duplicate "${t}" within a group`);
      }
      within.add(t);
    }
    for (const t of norm) {
      if (index.has(t)) {
        throw new Error(`word-relations: "${t}" appears in more than one group`);
      }
    }
    for (const t of norm) {
      index.set(
        t,
        norm.filter((other) => other !== t),
      );
    }
  }
  return index;
}

// Built once at module load on the curated groups (throws here if malformed).
const RELATION_INDEX = validateRelationGroups(WORD_RELATION_GROUPS);

/** The other group-members for `word` (normalized lookup); [] when none. */
export function relationsFor(word: string): string[] {
  return RELATION_INDEX.get(normalizeForSearch(word)) ?? [];
}
